import React, { useState, useMemo } from "react";
import { Layout, Menu, Input, Button, Flex, Tooltip } from "antd";
import {
  Search01Icon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
} from "hugeicons-react";
import { buildMenuItems } from "./sidebarConfig";
import { useSelector } from "react-redux";
import { getCookie } from "../../../utils/cookieUtils";
import logoImg from "../../assets/logo/dealVoice.png";
import miniLogoImg from "../../assets/favicon/favicon.png";
import "./sidebar.css";

const { Sider } = Layout;

function Sidebar({
  role = "admin",
  isCrmAdmin = false,
  activeTab = "overview",
  onTabChange,
  collapsed = false,
  onCollapseChange,
  showSearch = true,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isMinimized, setIsMinimized] = useState(collapsed);

  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  // Synchronize internal and external collapse states
  const handleToggleCollapse = () => {
    const nextState = !isMinimized;
    setIsMinimized(nextState);
    if (onCollapseChange) {
      onCollapseChange(nextState);
    }
  };

  // Build role-driven, search-filtered menu items
  const menuItems = useMemo(() => {
    return buildMenuItems({
      role,
      isCrmAdmin,
      searchTerm,
      companyCode: effectiveCompanyCode,
    });
  }, [role, isCrmAdmin, searchTerm, effectiveCompanyCode]);

  const handleMenuClick = ({ key }) => {
    if (onTabChange) {
      onTabChange(key);
    }
  };

  return (
    <Sider
      collapsible
      collapsed={isMinimized}
      trigger={null}
      width={210}
      collapsedWidth={70}
      className="crm-sidebar-sider"
      theme="light"
    >
      <Flex vertical className="sidebar-top-section" style={{ height: "100%" }}>
        {/* Brand Logo Header */}
        <Flex
          align="center"
          justify={isMinimized ? "center" : "space-between"}
          className="sidebar-brand-header"
        >
          {!isMinimized ? (
            <img src={logoImg} alt="DealVoice Logo" className="sidebar-brand-logo" />
          ) : (
            <img src={miniLogoImg} alt="Logo" className="sidebar-mini-logo" />
          )}
        </Flex>

        {/* Search & Collapse Toolbar on Same Line (3/4 Search, 1/4 Collapse) */}
        {!isMinimized ? (
          <div className="sidebar-search-row">
            {showSearch ? (
              <>
                <div className="sidebar-search-34">
                  <Input
                    prefix={<Search01Icon size={14} strokeWidth={1.6} style={{ color: "#94a3b8" }} />}
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                    className="sidebar-feature-search-input"
                  />
                </div>
                <div className="sidebar-collapse-14">
                  <Tooltip title="Collapse Sidebar" placement="right">
                    <Button
                      type="text"
                      icon={<PanelLeftCloseIcon size={16} strokeWidth={1.6} />}
                      onClick={handleToggleCollapse}
                      className="sidebar-collapse-icon-btn"
                    />
                  </Tooltip>
                </div>
              </>
            ) : (
              <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                <Tooltip title="Collapse Sidebar" placement="right">
                  <Button
                    type="text"
                    icon={<PanelLeftCloseIcon size={16} strokeWidth={1.6} />}
                    onClick={handleToggleCollapse}
                    className="sidebar-collapse-icon-btn"
                  />
                </Tooltip>
              </div>
            )}
          </div>
        ) : (
          <div className="sidebar-collapsed-toggle-wrap">
            <Tooltip title="Expand Sidebar" placement="right">
              <Button
                type="text"
                icon={<PanelLeftOpenIcon size={16} strokeWidth={1.6} />}
                onClick={handleToggleCollapse}
                className="sidebar-collapse-icon-btn"
              />
            </Tooltip>
          </div>
        )}

        {/* Navigation Menu */}
        <div className="sidebar-menu-scroll-container hide-scrollbar">
          <Menu
            mode="inline"
            inlineCollapsed={isMinimized}
            selectedKeys={[activeTab]}
            defaultOpenKeys={["crm_folder"]}
            onClick={handleMenuClick}
            items={menuItems}
            className="sidebar-antd-menu"
          />
        </div>
      </Flex>
    </Sider>
  );
}

export default Sidebar;
